import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWindowDimensions } from 'react-native';
import { useOrg } from '../hooks/useOrg';
import ListsManager from '../services/ListsManager';
import { FileText } from 'lucide-react';
import DailyReportView, { REPORT_STYLES, formatDate } from '../components/DailyReportView';

// Dashboard daily-reports screen: sidebar report list + the shared <DailyReportView>.
// All report rendering lives in DailyReportView (also used by the standalone
// login-free PublicReportPage); this screen only owns the list, data loading,
// and the live toggle.
const DailyReportsScreen = () => {
  const { reportDate } = useParams();
  const navigate = useNavigate();
  const { activeOrg } = useOrg();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [selectedReportDate, setSelectedReportDate] = useState(reportDate || null);
  const [liveMode, setLiveMode] = useState(false);
  const [showDetail, setShowDetail] = useState(!!reportDate);
  const [sidebarW, setSidebarW] = useState(280);
  const dragRef = useRef(null);
  const dragStartRef = useRef(null);

  // Load reports list
  useEffect(() => {
    let cancelled = false;
    const loadList = async () => {
      setLoading(true);
      setError(null);
      try {
        if (activeOrg?.id) {
          const result = await ListsManager.getDailyReports(activeOrg.id);
          if (cancelled) return;
          setReports(result.reports || []);
        }
      } catch (err) {
        if (!cancelled) setError('שגיאה בטעינת הדו״חות');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (activeOrg?.id) loadList();
    return () => { cancelled = true; };
  }, [activeOrg?.id]);

  // Load selected report
  useEffect(() => {
    let cancelled = false;
    const loadReport = async () => {
      if (!selectedReportDate || !activeOrg?.id) return;
      setReportLoading(true);
      try {
        const data = await ListsManager.getDailyReport(selectedReportDate, activeOrg.id, liveMode);
        if (cancelled) return;
        setReportData(data || null);
      } catch (err) {
        if (!cancelled) setReportData(null);
      } finally {
        if (!cancelled) setReportLoading(false);
      }
    };
    if (selectedReportDate && activeOrg?.id) loadReport();
    return () => { cancelled = true; };
  }, [selectedReportDate, activeOrg?.id, liveMode]);

  const handleSelectReport = (date) => {
    setSelectedReportDate(date);
    setReportData(null);
    setLiveMode(false);
    navigate(`/reports/${date}`, { replace: true });
    if (isMobile) setShowDetail(true);
  };

  const handleBack = () => {
    setShowDetail(false);
  };

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = true;
    dragStartRef.current = { x: e.clientX, w: sidebarW };
    const onMove = (mv) => {
      const dx = -(mv.clientX - dragStartRef.current.x);
      setSidebarW(Math.max(200, Math.min(420, dragStartRef.current.w + dx)));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const sidebarJsx = (
    <div className="rp-sidebar" style={{ width: isMobile ? '100%' : sidebarW, minWidth: isMobile ? undefined : sidebarW }}>
      <div className="rp-sidebar__header">
        <h1 className="rp-sidebar__title">דו״חות יומיים</h1>
        <div className="rp-sidebar__subtitle">{activeOrg?.name}</div>
      </div>
      <div className="rp-sidebar__list">
        {loading ? (
          <div className="rp-sidebar__loading">
            <div className="rp-spinner" />
          </div>
        ) : error ? (
          <div className="rp-sidebar__error">{error}</div>
        ) : reports.length === 0 ? (
          <div className="rp-sidebar__empty">
            <FileText size={36} color="#94a3b8" />
            <span>אין דו״חות עדיין</span>
          </div>
        ) : (
          reports.map(report => (
            <div
              key={report.id}
              className={`rp-report-item ${selectedReportDate === report.report_date ? 'rp-report-item--selected' : ''}`}
              onClick={() => handleSelectReport(report.report_date)}
            >
              <div className="rp-report-item__date">{formatDate(report.report_date)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderDetailPanel = () => {
    if (!selectedReportDate) {
      return (
        <div className="rp-detail rp-detail--empty">
          <FileText size={48} color="#94a3b8" />
          <div className="rp-detail__empty-text">בחר דו״ח מהרשימה</div>
        </div>
      );
    }
    if (reportLoading) {
      return (
        <div className="rp-detail rp-detail--empty">
          <div className="rp-spinner" />
          <div className="rp-detail__empty-text">טוען דו״ח...</div>
        </div>
      );
    }
    if (!reportData) {
      return (
        <div className="rp-detail rp-detail--empty">
          <div className="rp-detail__empty-text">הדו״ח לא נמצא</div>
        </div>
      );
    }
    return (
      <DailyReportView
        reportData={reportData}
        isMobile={isMobile}
        onBack={handleBack}
        liveControls={{
          liveMode,
          onToggleLive: () => setLiveMode(v => !v),
          loading: reportLoading,
        }}
      />
    );
  };

  return (
    <div className="rp-page">
      <style>{REPORT_STYLES}</style>
      {isMobile ? (
        showDetail ? renderDetailPanel() : sidebarJsx
      ) : (
        <>
          {sidebarJsx}
          <div className="rp-divider" onMouseDown={startDrag} />
          {renderDetailPanel()}
        </>
      )}
    </div>
  );
};

export default DailyReportsScreen;
